import { Activity, BbchStageBase, PesticideIndication } from 'farmdok-rest-api';

import notNullOrUndefined from '@/shared/modules/notNullOrUndefinedFilter';

import { sanitizeBbchCode } from './bbchCode';

type ActivityWithProducts = Pick<Activity, 'products'>;
type ActivityProductForBbchFiltering = Activity['products'][number];

function hasProductIdAndIndicationId(
  activityProduct: ActivityProductForBbchFiltering,
): activityProduct is ActivityProductForBbchFiltering & { productId: string; pesticideIndicationId: string } {
  return Boolean(activityProduct.productId && activityProduct.pesticideIndicationId);
}

export function getProductIdsForPesticideIndicationFilter(activities: ActivityWithProducts[]): string[] {
  const productIds = activities
    .flatMap((activity) => activity.products)
    .filter(hasProductIdAndIndicationId)
    .map((activityProduct) => activityProduct.productId);

  return [...new Set(productIds)];
}

export function getSelectedPesticideIndications(
  activities: ActivityWithProducts[],
  pesticideIndicationsByProduct: Record<string, PesticideIndication[] | undefined>,
): PesticideIndication[] {
  const indications: PesticideIndication[] = [];
  const indicationIds = new Set<string>();

  activities.forEach((activity) => {
    activity.products.filter(hasProductIdAndIndicationId).forEach((activityProduct) => {
      const pesticideIndication = pesticideIndicationsByProduct[activityProduct.productId]?.find(
        (candidate) => candidate.id === activityProduct.pesticideIndicationId,
      );
      if (!pesticideIndication) {
        return;
      }

      const indicationKey = `${activityProduct.productId}:${pesticideIndication.id}`;
      if (indicationIds.has(indicationKey)) {
        return;
      }

      indications.push(pesticideIndication);
      indicationIds.add(indicationKey);
    });
  });

  return indications;
}

export function filterBbchStages(
  bbchStages: BbchStageBase[],
  selectedBbchGroupIds: string[],
  selectedPesticideIndications: PesticideIndication[],
): BbchStageBase[] {
  return bbchStages
    .filter((stage) => {
      const bbchCode = sanitizeBbchCode(stage.bbchCode);
      if (bbchCode === null) {
        return false;
      }

      if (selectedBbchGroupIds.length > 0 && !selectedBbchGroupIds.includes(stage.bbchGroupId || '')) {
        return false;
      }

      return selectedPesticideIndications.every((indication) => indicationAllowsBbchCode(indication, bbchCode));
    })
    .sort(
      (a, b) =>
        (a.bbchCode ?? Number.POSITIVE_INFINITY) - (b.bbchCode ?? Number.POSITIVE_INFINITY) ||
        (a.description ?? '').localeCompare(b.description ?? ''),
    );
}

export function indicationAllowsBbchCode(indication: PesticideIndication, bbchCode: number): boolean {
  const bbchRanges = indication.bbchStages
    ?.filter((bbchStage) => bbchStage.lowerBound !== null || bbchStage.upperBound !== null)
    .map((bbchStage) => {
      const lowerBound = sanitizeBbchCode(bbchStage.lowerBound ?? 0);
      const upperBound = sanitizeBbchCode(bbchStage.upperBound ?? 99);
      if (lowerBound === null || upperBound === null) {
        return null;
      }

      return {
        lowerBound: Math.min(lowerBound, upperBound),
        upperBound: Math.max(lowerBound, upperBound),
      };
    })
    .filter(notNullOrUndefined);

  if (!bbchRanges || bbchRanges.length < 1) {
    return true;
  }

  return bbchRanges.some((bbchRange) => bbchCode >= bbchRange.lowerBound && bbchCode <= bbchRange.upperBound);
}
